import { useState, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Link,
  Typography,
  Snackbar,
  IconButton,
  InputAdornment
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { useHistory, useLocation } from "react-router-dom";
import url from "../../constants/urlList";
import { readUrlSecrets } from "../../utils/url-secret-scrub";
const pattern = /\S+@\S+\.\S+/i;
const emailImg = require("../../images/email.png").default;

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

const ForgetPwd = ({ hasId }) => {
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [error, setError] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [open, setOpen] = useState(false);
  const [apiError, setApiError] = useState('');
  // Show/hide toggles for the reset-password fields (parity with register).
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const history = useHistory();
  const query = useQuery();
  const { pathname } = useLocation();
  // F1: the reset link's token and email are moved out of the URL by the head
  // script in public/index.html before tracking runs. Read them from that
  // stash once; readUrlSecrets falls back to the query (and scrubs it) when
  // the head script could not stash.
  const [urlSecrets] = useState(() => readUrlSecrets(pathname, ["token", "email"]).values);
  const token = urlSecrets.token ?? query.get("token");
  const emailId = urlSecrets.email ?? query.get("email");

  const setEmailText = (evt) => {
    const val = evt.target.value.trim();
    setEmail(val);

    if (!pattern.test(val)) {
      setError('Email not valid');
    } else {
      setError('');
    }
  };

  const onPasswordChange = (evt, type) => {
    const val = evt.target.value.trim();

    if (type === 'pwd') {
      setPwd(val);
    } else {
      setConfirmPwd(val);
    }

    if (val.length < 6) {
      setError('Password must be of 6 characters');
      return;
    }

    // Compare the field being edited against the OTHER field's current value.
    // (The old code compared the just-edited field against its own stale state,
    // which left a "does not match" error stuck after correcting the New
    // Password field — so the button stayed disabled while the match indicator
    // said the passwords matched.)
    const other = type === 'pwd' ? confirmPwd : pwd;
    if (other && other !== val) {
      setError('Password does not match');
    } else {
      setError('');
    }
  };

  const initiateForgetPwd = () => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    };

    return fetch(url.forgetPwd + email, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return setLinkSent(true);
        }

        return response.json();
      }).then(res => {
        if (res?.message) {
          setApiError(res?.message);
          setOpen(true);
        }
      })
      .catch((err) => {
        console.log("Forget password err");
      });
  };

  const changePassword = () => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: emailId,
        password: pwd
      })
    };

    return fetch(url.changePwd + token, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          history.push('/login');
        }
      })
      .catch(() => {
        console.log("Forget password err");
      });
  };

  const goToLogin = (e) => {
    e.stopPropagation();
    history.push("/login");
  };

  const handleClose = () => {
    setOpen(false);

    setTimeout(() => setApiError(''), 300);
  };

  return (
    <Box className="login-body">
      <Snackbar open={open} onClose={handleClose} autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <>
          {apiError &&
            <Box className="sub-alert">
              {apiError}
            </Box>
          }
          {!apiError &&
            <Box className="sub-alert">
              Something went wrong...
            </Box>
          }
        </>
      </Snackbar>
      <Box component="div" className="app-name">
        QLearn
      </Box>
      <Box component="div" className="login-container">
        <Box className="login-form">
          {!hasId && !linkSent &&
            <>
              <TextField
                className="forgot-email-field"
                label="Email"
                onChange={setEmailText}
                value={email}
              />
              <Typography className="error">{error}</Typography>
              <Button
                variant="contained"
                color="primary"
                className="register-btn"
                disabled={email.length === 0 || error.length > 0}
                onClick={initiateForgetPwd}
              >
                Send Reset Link
              </Button>
              <Link onClick={goToLogin} variant="body2" rel="noopener">
                Login
              </Link>
            </>
          }
          {!hasId && linkSent &&
            <Box className="mail-sent">
              <img alt="email" src={emailImg} width='100' height='100'></img>
              <Typography>
                A link has been sent to your mail. Please open that link to reset password.
              </Typography>
            </Box>
          }

          {hasId &&
            <>
              <TextField
                label="New Password"
                onChange={(evt) => onPasswordChange(evt, 'pwd')}
                value={pwd}
                type={showPwd ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPwd ? "Hide password" : "Show password"}
                        onClick={() => setShowPwd((show) => !show)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        size="small"
                      >
                        {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm Password"
                onChange={(evt) => onPasswordChange(evt, 'confirmPwd')}
                value={confirmPwd}
                type={showConfirmPwd ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showConfirmPwd ? "Hide confirm password" : "Show confirm password"}
                        onClick={() => setShowConfirmPwd((show) => !show)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {/* Positive confirmation once the two passwords match (computed
                  directly from the two values, so it can't co-occur with the
                  red error); otherwise show the shared validation error. */}
              {pwd && confirmPwd && pwd === confirmPwd && pwd.length >= 6
                ? <Typography className="error" style={{ color: '#2e7d32' }}><span aria-hidden="true">✓ </span>Passwords match</Typography>
                : error
                  ? <Typography className="error">{error}</Typography>
                  // Show the length requirement up front (grey, not error-red) so
                  // users aren't guessing before the first validation error.
                  : <Typography className="error" style={{ color: 'var(--sub-text, #757575)' }}>Use at least 6 characters</Typography>
              }
              <Button
                variant="contained"
                color="primary"
                className="register-btn"
                disabled={confirmPwd.length === 0 || pwd.length === 0 || error.length > 0}
                onClick={changePassword}
              >
                Change Password
              </Button>
            </>
          }
        </Box>
      </Box>
    </Box>
  );
};

export default ForgetPwd;
